// PayGate Operations dashboard (admin-portal) — payout throughput, connector/rail health,
// and reconciliation state at a glance. Read-only aggregates from paygate-service metrics
// (+ routing providers for health). Optional tenant filter; blank = platform-wide.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fmtDateTime } from '../../lib/format';
import {
  Alert, Button, Card, Col, Input, Row, Segmented, Space, Statistic, Table, Tag, Typography, message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import * as api from '../../services/paygateMetricsService';
import { listProviders, type ProviderStatus, type Health } from '../../services/routingService';

const { Title, Text, Paragraph } = Typography;
const ORANGE = '#F28A3A';
const GREEN = '#16A34A';
const RED = '#DC2626';
const BLUE = '#2563EB';
const HEALTH_COLOR: Record<Health, string> = { healthy: 'green', degraded: 'gold', unavailable: 'red' };

const errMsg = (e: any, fb: string) =>
  e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fb;
const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n || 0);

export const PaymentOpsDashboardPage: React.FC = () => {
  const [tenantId, setTenantId] = useState('');
  const [applied, setApplied] = useState(''); // the tenantId actually queried
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<api.Summary | null>(null);
  const [points, setPoints] = useState<api.DayPoint[]>([]);
  const [connectors, setConnectors] = useState<api.ConnectorRow[]>([]);
  const [recon, setRecon] = useState<api.StatusCount[]>([]);
  const [providers, setProviders] = useState<ProviderStatus[]>([]);
  const [held, setHeld] = useState<api.HeldBatch[]>([]);

  const load = useCallback(async (tid: string, d: number) => {
    setLoading(true);
    try {
      const [s, p, c, rc, pv, hb] = await Promise.all([
        api.getSummary(tid || undefined).catch(() => null),
        api.getThroughput(d, tid || undefined).catch(() => []),
        api.getByConnector(tid || undefined).catch(() => []),
        api.getReconciliation(tid || undefined).catch(() => []),
        listProviders().catch(() => []),
        api.listHeldBatches(tid || undefined).catch(() => []),
      ]);
      setSummary(s);
      setPoints(p);
      setConnectors(c);
      setRecon(rc);
      setProviders(pv);
      setHeld(hb);
      setApplied(tid);
    } catch (e) {
      message.error(errMsg(e, 'Could not load metrics'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load('', days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const healthByConnector = useMemo(() => {
    const m: Record<string, Health> = {};
    providers.forEach((p) => { m[p.provider] = p.health; });
    return m;
  }, [providers]);

  const connectorColumns = [
    {
      title: 'Connector',
      dataIndex: 'connector',
      render: (v: string) => {
        const h = healthByConnector[v];
        return (
          <Space>
            <Text strong>{v}</Text>
            {h && <Tag color={HEALTH_COLOR[h]} style={{ margin: 0 }}>{h}</Tag>}
          </Space>
        );
      },
    },
    { title: 'Total', dataIndex: 'total' },
    { title: 'Paid', dataIndex: 'paid', render: (v: number) => <Text style={{ color: GREEN }}>{v}</Text> },
    { title: 'Failed', dataIndex: 'failed', render: (v: number) => (v ? <Text style={{ color: RED }}>{v}</Text> : '0') },
    { title: 'Settled', dataIndex: 'amount', render: (v: number) => fmtMoney(v) },
  ];

  const heldColumns = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      render: (v: string) => (
        <Text copyable={{ text: v }} style={{ fontFamily: 'monospace', fontSize: 12 }}>{v.slice(0, 8)}…</Text>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source_system',
      render: (v: string, row: api.HeldBatch) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{row.source_reference}</Text>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      align: 'right' as const,
      render: (v: number, row: api.HeldBatch) => `${fmtMoney(v)} ${row.currency}`,
    },
    { title: 'Payments', dataIndex: 'item_count', align: 'center' as const },
    {
      title: 'Held because',
      dataIndex: 'hold_reason',
      render: (v?: string) => (v ? <Tag color="orange" style={{ whiteSpace: 'normal' }}>{v}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: 'Waiting since',
      dataIndex: 'created_at',
      render: (v: string) => {
        const d = new Date(v);
        return isNaN(d.getTime()) ? '—' : fmtDateTime(d);
      },
    },
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>Payment Operations</Title>
          <Paragraph type="secondary" style={{ maxWidth: 720 }}>
            Payout throughput, connector/rail health, and reconciliation state.
            {applied ? ` Scoped to tenant ${applied}.` : ' Platform-wide across all tenants.'}
          </Paragraph>
        </div>
        <Space wrap>
          <Input
            style={{ width: 300 }}
            placeholder="tenant UUID (blank = all tenants)"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            onPressEnter={() => load(tenantId.trim(), days)}
            allowClear
          />
          <Segmented
            value={days}
            onChange={(v) => { setDays(v as number); load(tenantId.trim(), v as number); }}
            options={[{ label: '7d', value: 7 }, { label: '14d', value: 14 }, { label: '30d', value: 30 }]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => load(tenantId.trim(), days)}>Refresh</Button>
        </Space>
      </Space>

      <Row gutter={16} style={{ margin: '8px 0 16px' }}>
        <Col xs={12} md={4}><Card size="small" loading={loading}><Statistic title="Payout items" value={summary?.total_items ?? 0} /></Card></Col>
        <Col xs={12} md={4}><Card size="small" loading={loading}><Statistic title="Paid" value={summary?.paid ?? 0} valueStyle={{ color: 'var(--color-success)' }} /></Card></Col>
        <Col xs={12} md={4}><Card size="small" loading={loading}><Statistic title="Failed" value={summary?.failed ?? 0} valueStyle={{ color: 'var(--color-danger)' }} /></Card></Col>
        <Col xs={12} md={4}><Card size="small" loading={loading}><Statistic title="In flight" value={summary?.in_flight ?? 0} valueStyle={{ color: 'var(--color-warning)' }} /></Card></Col>
        <Col xs={12} md={4}><Card size="small" loading={loading}><Statistic title="Settled" value={fmtMoney(summary?.settled_amount ?? 0)} /></Card></Col>
        <Col xs={12} md={4}><Card size="small" loading={loading}><Statistic title="Open recon" value={summary?.open_recon_cases ?? 0} valueStyle={summary?.open_recon_cases ? { color: 'var(--color-danger)' } : undefined} /></Card></Col>
      </Row>

      <Card
        size="small"
        title={<Space>Held for verification{held.length ? <Tag color="orange">{held.length}</Tag> : null}</Space>}
        style={{ marginBottom: 16 }}
        loading={loading}
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Payout batches held before dispatch — by a per-tenant payout limit or the dual-control gate.
          Oldest first. Release happens in the tenant&apos;s own Payouts workspace; this is a read-only watch.
        </Paragraph>
        {held.length ? (
          <Table
            rowKey="id"
            size="small"
            columns={heldColumns as any}
            dataSource={held}
            pagination={held.length > 10 ? { pageSize: 10 } : false}
          />
        ) : (
          <Alert type="success" showIcon message={applied ? 'No held batches for this tenant.' : 'No batches are held across any tenant.'} />
        )}
      </Card>

      <Card size="small" title={`Throughput — last ${days} days`} style={{ marginBottom: 16 }} loading={loading}>
        {points.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e5e7eb)" />
              <XAxis dataKey="day" fontSize={11} />
              <YAxis yAxisId="left" fontSize={11} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" fontSize={11} tickFormatter={fmtMoney} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="paid" name="Paid" stackId="a" fill={GREEN} />
              <Bar yAxisId="left" dataKey="failed" name="Failed" stackId="a" fill={RED} />
              <Bar yAxisId="left" dataKey="in_flight" name="In flight" stackId="a" fill={BLUE} />
              <Line yAxisId="right" type="monotone" dataKey="amount" name="Settled amount" stroke={ORANGE} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <Alert type="info" showIcon message="No payout activity in this window." />
        )}
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card size="small" title="By connector / rail" loading={loading}>
            <Table rowKey="connector" size="small" columns={connectorColumns as any} dataSource={connectors} pagination={false}
              locale={{ emptyText: 'No payouts yet' }} />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card size="small" title="Open reconciliation cases" loading={loading}>
            {recon.length ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {recon.map((c) => (
                  <Space key={c.status} style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text>{c.status.replace(/_/g, ' ')}</Text>
                    <Tag color={c.status.includes('review') || c.status.includes('mismatch') ? 'red' : 'orange'}>{c.count}</Tag>
                  </Space>
                ))}
              </Space>
            ) : (
              <Alert type="success" showIcon message="No open reconciliation cases." />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PaymentOpsDashboardPage;
